
import { Component, Vue } from "vue-property-decorator";
import Search from "@/components/reusable/Search.vue";
import Pagination from "@/components/reusable/table/Pagination.vue";
import Icon from "@/components/reusable/Icon.vue";
import { AuthError } from "@/services/error_service";
import UIkit from "uikit";
import AttributeService from "@/services/attribute_service";
//import Toast from "@/components/reusable/Toast.vue";
import { EventBus } from "@/events/index";
import AttributeTable from "@/components/attribute/AttributeTable.vue";
import { AttributeModel, AttributeRequestOptions } from "@/models/attribute";
import { APIDataResponse } from "@/models/api_res";
import { StoreModule } from "@/store/types";
import { GlobalActions, GlobalGetters } from "@/store/modules/global/types";
import { namespace } from "vuex-class";
import ConfirmDelete from "../reusable/modals/ConfirmDelete.vue";
@Component({
  components: {
    AttributeTable,
    Search,
    Pagination,
    Icon,
    //Toast
  },
})
export default class Attribute extends Vue {
  protected failed: string[] = [];
  protected success: string[] = [];
  protected deleteData: AttributeModel[] = [];
  protected toast = false;
  protected messageHtml = "";
  protected attributeService = new AttributeService();
  protected attributes: AttributeModel[] = [];
  protected disableDrag = true;
  protected pages = 1;
  protected currentPage = 1;
  protected query = "";
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetLoading))
  isLoading!: boolean;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddLoading))
  setLoading: any;

  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetBusinessUnit))
  businessUnit!: string;

  created() {
    this.setLoading(true);
    if (this.$route.query.q) {
      this.disableDrag = true;
    }
    const options: AttributeRequestOptions = this.getRequestOptions();
    this.getAttributes(options);
  }

  mounted() {
    EventBus.$on(
      "deleteConfirmed",
      (id: number, name: string, final = false) => {
        this.deleteRequest(id, name, final);
      }
    );
    /** Global event listener for data deletion. Triggers & sends array of data selected for deletion through to confirmation modal.
     * This event is called from the <Delete> component (a child in the corresponding <Menu> component [@ex: <ProductMenu>, <MfrMenu>...]) and from the base <Table> component.
     */
    EventBus.$on("deleteRow", (data: AttributeModel[]) => {
      this.deleteData = data;
      this.$modal.show(
        ConfirmDelete,
        { deleteData: this.deleteData, type: "attribute" },
        { height: "auto", adaptive: true, classes: "overflow" }
      );
    });
    /** Restore a soft-deleted attribute. Emitted from the <Revive> row menu option. */
    EventBus.$on("reviveRow", (id: number, name: string) => {
      this.reviveRequest(id, name);
    });
  }

  beforeDestroy() {
    EventBus.$off("deleteConfirmed");
    EventBus.$off("deleteRow");
    EventBus.$off("reviveRow");

    /** UIkit modals do not leave the DOM unless explicitly destroyed. Destroying them helps with buggy functionality due to dynamic data. This method loops through all of the modal ids and remove
     * them from the DOM upon vue's beforeDestroy() lifecycle hook.
     *
     * Not that typescript does not have definitions for many UIkit methods, hence //@ts-ignore flag.
     */
    const modals = [
      "#delete-modal",
      "#move-modal",
      "#confirm-moving-modal",
      "#add-model",
      "#save-modal",
    ];
    modals.forEach((selector) => {
      const component = UIkit.modal(selector);
      if (component) {
        //@ts-ignore
        component.$destroy(true);
      }
    });
  }

  protected async getAttributes(
    optionsObject?: AttributeRequestOptions
  ): Promise<void> {
    try {
      // The /attributes endpoint ignores `bu`, so we don't send it.
      const res: APIDataResponse = await this.attributeService.getAttributes(
        optionsObject
      );
      this.attributes = res.results;
      this.pages = res.meta.total_pages;
      this.currentPage = res.meta.page;
      this.setLoading(false);
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      } else {
        EventBus.$emit("showError", err.message);
      }
    }
  }

  /**
   * Paginate method triggered by child <Pagination> component.
   * First, calls this.getRequestOptions() to retain any other options (like query term)
   * NOTE: page property in returned options object must be overwritten -- this.getRequestOptions will return the current page.
   *
   * Second, send options object into API get request to get appropriate page of data.
   *
   * Finally, add page query to url if it is not already there. This ensure the correct page query is retained and will fetch correct data if user refreshes.
   */
  protected paginate(page: number) {
    // Only push the page into the URL. The <router-view :key="$route.fullPath">
    // in Main.vue recreates this component on the query change, and created()
    // re-fetches via getRequestOptions(). Calling getAttributes() here too would
    // double-fetch and race.
    if (!this.$route.query.page || this.$route.query.page !== page.toString()) {
      this.$router.push({
        query: { ...this.$route.query, page: page.toString() },
      });
    }
  }

  public receiveSearchTerm(query: string): void {
    if (this.query !== query) {
      this.query = query;
      // A new search must start at page 1 — drop any existing page param,
      // otherwise searching from a deep page lands on an empty result page.
      const routeQuery = { ...this.$route.query };
      delete routeQuery.page;
      routeQuery.q = query;
      this.$router.push({ query: routeQuery });
    }
  }

  /**
   * Checks URL for current query terms. This should be called on every get request unless user is requesting unfiltered data
   * The object returned varies based on the endpoint being accessed. See model for each request for more details.
   */
  protected getRequestOptions(): AttributeRequestOptions {
    const optionsObject = {} as AttributeRequestOptions;
    if (this.query || this.$route.query.q) {
      this.query = this.$route.query.q
        ? (this.$route.query.q as string)
        : this.query;
      optionsObject.q = this.query;
    }
    if (this.$route.query.page) {
      optionsObject.page = parseInt(this.$route.query.page as string, 10);
    }
    if (this.$route.query.empty) {
      optionsObject.empty = true;
    }
    if (this.$route.query.show_deleted) {
      optionsObject.deleted = true;
    }
    return optionsObject;
  }

  /** Reset search by re-requesting unfiltered data */
  public reset(): void {
    this.query = "";
    const options = { ...this.$route.query };
    delete options.q;
    delete options.page;
    this.$router.push({ query: { ...options } });
  }

  /**
   * @param id id of item to be deleted
   * @param name name of item to be deleted, used in <Toast> confirmation
   * @param final optional, default: false, flags the final item in the request array; triggers <Toast> confirmation, refreshes data
   *
   * in the <{Path}Editor> component, @param final is not used.
   */
  protected async deleteRequest(
    id: number,
    name: string,
    final = false
  ): Promise<void> {
    this.setLoading(true);
    try {
      await this.attributeService.deleteAttribute(id);
      this.success.push(name);
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      } else {
        this.failed.push(name);
        // EventBus.$emit("showError", err.message);
      }
    }
    if (final) {
      const optionsObject = this.getRequestOptions();
      this.getAttributes(optionsObject);
      if (this.success.length > 0) {
        EventBus.$emit(
          "showSuccess",
          `Attributes have been deleted successfully.`,
          []
        );
      }
      if (this.failed.length > 0) {
        EventBus.$emit(
          "showFail",
          `The delete request for the following ${
            this.failed.length > 1 ? "attributes" : "attribute"
          } failed. Please try again.`,
          this.failed
        );
      }
      this.success = [];
      this.failed = [];
      this.deleteData = [];
    }
  }

  /**
   * Restore a soft-deleted attribute, then refresh the current list view.
   * @param id id of the attribute to revive
   * @param name display name, used in the confirmation toast
   */
  protected async reviveRequest(id: number, name: string): Promise<void> {
    this.setLoading(true);
    try {
      await this.attributeService.reviveAttribute(id);
      this.getAttributes(this.getRequestOptions());
      EventBus.$emit(
        "showSuccess",
        `${name} has been restored successfully.`,
        []
      );
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      } else {
        this.setLoading(false);
        EventBus.$emit("showError", err.message);
      }
    }
  }

  protected closeToast(): void {
    this.toast = false;
    this.failed = [];
    this.success = [];
  }
  protected showToast(msg: string): void {
    this.messageHtml = msg;
    this.toast = true;
  }
}
